import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import Slider from 'react-slick';
import Modal from 'react-modal';
import { useParams, useNavigate } from 'react-router-dom';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import './AllProperties.css';
import TopBar from '../Layouts/TopBar';
import Footer from '../Layouts/Footer';
import { TroubleshootOutlined } from '@mui/icons-material';
import { FaWhatsapp, FaEnvelope, FaSms } from 'react-icons/fa';

const SkeletonStyles = () => (
  <style>{`
    .skeleton { position: relative; overflow: hidden; background: #e9eef5; }
    .skeleton::after { content: ''; position: absolute; top: 0; left: -150px; bottom: 0; width: 150px; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent); animation: shimmer 1.2s infinite; }
    @keyframes shimmer { 0% { transform: translateX(0); } 100% { transform: translateX(300%); } }
  `}</style>
);

const base_url = 'https://api.homehive.co.zw/api';

const Property = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [property, setProperty] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [related, setRelated] = useState([]);
  const [isLoadingRelated, setIsLoadingRelated] = useState(false);
  const [nextUrl, setNextUrl] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const currentUser = JSON.parse(localStorage.getItem('user'));

  const [subscriptionPlans] = useState([
    {
      id: 1,
      name: 'Monthly Access',
      price: 0.01,
      duration: '/month',
      features: [
        'Contact unlimited landlords',
        'Chat with property owners',
        'Schedule viewings',
      ]
    }
  ]);

  const [paymentMethod, setPaymentMethod] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentCheckInterval, setPaymentCheckInterval] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const token = localStorage.getItem('token');
  const [isAddingFavorite, setIsAddingFavorite] = useState(false);

  const [paymentFormData, setPaymentFormData] = useState({
    amount: subscriptionPlans[0].price,
    payment_method: '',
    phone: ''
  });

  const handleSubscriptionClick = () => {
    if (!currentUser) {
      Swal.fire({
        title: 'Authentication Required',
        text: 'Please log in to access premium features',
        icon: 'warning',
        confirmButtonColor: '#1a237e',
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Login'
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/login');
        }
      });
      return;
    }
    setShowSubscriptionModal(true);
  };

  const handleSubscribe = async (planId) => {
    if (!currentUser) {
      Swal.fire({
        title: 'Authentication Required',
        text: 'Please log in to subscribe',
        icon: 'warning',
        confirmButtonColor: '#1a237e',
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Login'
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/login');
        }
      });
      return;
    }

    if (!paymentMethod) {
      Swal.fire({
        title: 'Select Payment Method',
        text: 'Please select a payment method to continue',
        icon: 'warning',
        confirmButtonColor: '#1a237e'
      });
      return;
    }

    try {
      setIsProcessingPayment(true);

      // Validate amount
      if (!paymentFormData.amount || paymentFormData.amount <= 0) {
        throw new Error('Invalid payment amount');
      }

      // For EcoCash payments, get phone number
      let phoneNumber = '';
      if (paymentMethod === 'ecocash') {
        const result = await Swal.fire({
          title: 'Enter EcoCash Phone Number',
          input: 'tel',
          inputLabel: 'Phone Number',
          inputPlaceholder: 'Enter your EcoCash number',
          showCancelButton: true,
          inputValidator: (value) => {
            if (!value) {
              return 'Please enter your phone number';
            }
            // Basic Zimbabwe phone number validation
            const phoneRegex = /^(?:\+263|0)7[7-8][0-9]{7}$/;
            if (!phoneRegex.test(value)) {
              return 'Please enter a valid Zimbabwe phone number';
            }
          },
          confirmButtonColor: '#1a237e',
          cancelButtonColor: '#64748b'
        });

        if (result.dismiss) {
          setIsProcessingPayment(false);
          return;
        }

        phoneNumber = result.value;
        if (!phoneNumber) {
          setIsProcessingPayment(false);
          return;
        }
      }

      // Prepare payment data
      const paymentData = {
        amount: parseFloat(paymentFormData.amount.toString()),
        payment_method: paymentMethod
      };

      // Add phone number for EcoCash payments
      if (paymentMethod === 'ecocash') {
        paymentData.phone = phoneNumber;
      }

      // Initiate payment
      const response = await axios.post(
        `${base_url}/payment/`,
        paymentData,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.status === 'success') {
        const { reference, pollUrl, instructions } = response.data;

        if (paymentMethod === 'ecocash') {
          // Show loading dialog instead of confirmation button
          Swal.fire({
            title: 'Processing EcoCash Payment',
            text: 'Please check your phone for the payment prompt and enter your PIN to confirm.',
            icon: 'info',
            showConfirmButton: false,
            showCancelButton: true,
            cancelButtonText: 'Cancel Payment',
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            }
          });
        } else if (paymentMethod === 'visa') {
          // Redirect to payment gateway
          window.location.href = pollUrl;
          return;
        }

        // Start polling for payment status
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await axios.get(
              `${base_url}/payment/`,
              {
                params: {
                  reference: reference,
                  pollUrl: pollUrl
                },
                headers: { 'Authorization': `Bearer ${token}` }
              }
            );

            const { status, message } = statusResponse.data;

            if (status === 'paid') {
              clearInterval(pollInterval);
              setPaymentCheckInterval(null);
              setIsProcessingPayment(false);
              setShowSubscriptionModal(false);

              Swal.fire({
                title: 'Subscription Successful!',
                text: message || 'You can now contact landlords and access all features',
                icon: 'success',
                confirmButtonColor: '#1a237e'
              }).then(() => {
                // Refresh page to update subscription status
                window.location.reload();
              });
            } else if (status === 'cancelled' || status === 'failed') {
              clearInterval(pollInterval);
              setPaymentCheckInterval(null);
              setIsProcessingPayment(false);

              Swal.fire({
                title: 'Payment Failed',
                text: message || 'Payment was cancelled or failed. Please try again.',
                icon: 'error',
                confirmButtonColor: '#1a237e'
              });
            }
            // Continue polling for 'pending' status
          } catch (error) {
            console.error('Error checking payment status:', error);
            clearInterval(pollInterval);
            setPaymentCheckInterval(null);
            setIsProcessingPayment(false);

            Swal.fire({
              title: 'Error',
              text: error.response?.data?.detail || 'Failed to check payment status',
              icon: 'error',
              confirmButtonColor: '#1a237e'
            });
          }
        }, 5000);

        setPaymentCheckInterval(pollInterval);
      } else {
        throw new Error(response.data.detail || 'Failed to initiate payment');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setIsProcessingPayment(false);
      
      Swal.fire({
        title: 'Payment Error',
        text: error.response?.data?.detail || error.message || 'Payment initiation failed',
        icon: 'error',
        confirmButtonColor: '#1a237e'
      });
    }
  };

  // Update payment method selection in subscription modal
  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    setPaymentFormData(prev => ({
      ...prev,
      payment_method: method,
      phone: method === 'ecocash' ? prev.phone : ''
    }));
  };

  const checkExistingConversation = async (propertyId, landlordId) => {
    try {
      const response = await axios.get(
        `${base_url}/conversations/`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const existingConversation = response.data.find(conv =>
        conv.property?.id === propertyId &&
        conv.participants.some(p => p.id === landlordId)
      );

      return existingConversation;
    } catch (error) {
      console.error('Error checking conversations:', error);
      return null;
    }
  };

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (paymentCheckInterval) {
        clearInterval(paymentCheckInterval);
      }
    };
  }, [paymentCheckInterval]);

  useEffect(() => {
    const fetchProfile = async () => {
      const response = await axios.get(`${base_url}/user/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log("profile", response.data);
      setProfile(response.data);
    };
    fetchProfile();
  }, [token]);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await axios.get(`${base_url}/properties/${id}`);
        const data = response?.data;
        setProperty(data?.id ? data : (data?.results ? data.results[0] : data));
      } catch (error) {
        console.error('Error loading property:', error);
        Swal.fire({ title: 'Error', text: 'Failed to load property details', icon: 'error', confirmButtonColor: '#1a237e' })
          .then(() => navigate(-1));
      } finally {
        setIsLoading(false);
      }
    };
    if (id) fetchProperty();
  }, [id, navigate]);

  useEffect(() => {
    const fetchRelated = async () => {
      if (!property) return;
      try {
        setIsLoadingRelated(true);
        const params = {};
        // Filter by same property type only
        if (property.property_type) params.type = property.property_type;
        params.page_size = 6;
        const response = await axios.get(`${base_url}/properties`, { params });
        const list = Array.isArray(response?.data?.results) ? response.data.results : (Array.isArray(response?.data) ? response.data : []);
        const filtered = list.filter(p => p.id !== property.id);
        setRelated(filtered);
        setNextUrl(response?.data?.next);
        console.log("related", filtered);
        console.log("params", params);
        console.log("nextUrl", response?.data?.next);
      } catch (e) {
        console.error('Failed to load related properties', e);
      } finally {
        setIsLoadingRelated(false);
      }
    };
    fetchRelated();
  }, [property]);

  const loadMoreRelated = async () => {
    if (!nextUrl || isLoadingMore) return;
    
    try {
      setIsLoadingMore(true);
      const response = await axios.get(nextUrl);
      const list = Array.isArray(response?.data?.results) ? response.data.results : (Array.isArray(response?.data) ? response.data : []);
      const filtered = list.filter(p => p.id !== property.id);
      setRelated(prev => [...prev, ...filtered]);
      setNextUrl(response?.data?.next);
    } catch (e) {
      console.error('Failed to load more related properties', e);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleScroll = () => {
    if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1000) {
      loadMoreRelated();
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [nextUrl, isLoadingMore]);

  const formatPrice = (price) => {
    const num = typeof price === 'string' ? parseFloat(price) : Number(price);
    const safe = Number.isFinite(num) ? num : 0;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(safe);
  };

  const handleShare = () => {
    const shareData = {
      title: `Check out this property on Home Hive \n\n${property?.property_type_display} in ${property?.location} \n\n${property?.description?.slice(0, 120)} \n\n${window.location.href}` || 'Home Hive Property',
      url: window.location.href
    };
    if (navigator.share) {
      navigator.share(shareData).catch(() => {});
      return;
    }
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(`${shareData.title}`);
    const options = [
      { label: 'WhatsApp', href: `https://wa.me/?text=${text}` },
      { label: 'Email', href: `mailto:?subject=${encodeURIComponent(shareData.title)}&body=${text}` },
      { label: 'SMS', href: `sms:?&body=${text}` },
    ];
    Swal.fire({
      title: 'Share Property',
      html: options.map(o => `<a href="${o.href}" target="_blank" rel="noreferrer" class="swal2-confirm swal2-styled" style="margin:6px;">${o.label}</a>`).join(''),
      showConfirmButton: false,
    });
  };

  const handleAddToFavorites = async () => {
    if (!currentUser) {
      Swal.fire({
        title: 'Login Required',
        text: 'Please log in to save favourites',
        icon: 'info',
        confirmButtonColor: '#1a237e'
      }).then(() => navigate('/login'));
      return;
    }
    try {
      setIsAddingFavorite(true);
      await axios.post(`${base_url}/favourite-properties/`, { property: property.id }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      Swal.fire({ title: 'Added to favourites', icon: 'success', timer: 1400, showConfirmButton: false });
    } catch (e) {
      console.error('Add favourite failed', e);
      const msg = e.response?.data?.detail || 'Failed to add to favourites';
      Swal.fire({ title: 'Error', text: msg, icon: 'error', confirmButtonColor: '#1a237e' });
    } finally {
      setIsAddingFavorite(false);
    }
  };

  const handleContactClick = async (action, propertyId) => {
    if (!currentUser) {
      Swal.fire({
        title: 'Authentication Required',
        text: 'Please log in to contact the landlord',
        icon: 'warning',
        confirmButtonColor: '#1a237e',
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Login'
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/login');
        }
      });
      return;
    }

    // Check if user is subscribed
    if (!profile?.user?.is_subscribed) {
      Swal.fire({
        title: 'Subscription Required',
        text: 'You need to subscribe to contact landlords. Would you like to subscribe now?',
        icon: 'info',
        confirmButtonColor: '#1a237e',
        showCancelButton: true,
        cancelButtonText: 'Not Now',
        confirmButtonText: 'Subscribe'
      }).then((result) => {
        if (result.isConfirmed) {
          setShowSubscriptionModal(true);
        }
      });
      return;
    }

    // Handle different contact actions
    switch (action) {
      case 'chat':
        if (!property) return;

        setChatLoading(true);
        try {
          const existingConversation = await checkExistingConversation(
            propertyId,
            property.owner.id
          );

          if (existingConversation) {
            // If conversation exists, navigate to existing chat
            navigate(`/chat/${existingConversation.id}`);
          } else {
            // If no conversation exists, create new one
            const response = await axios.post(
              `${base_url}/conversations/`,
              {
                property_id: propertyId,
                user_id: property.owner.id
              },
              {
                headers: { 'Authorization': `Bearer ${token}` }
              }
            );

            // Navigate to new chat
            navigate(`/chat/${response.data.id}`);
          }
        } catch (error) {
          console.error('Error handling chat:', error);
          Swal.fire({
            title: 'Error',
            text: 'Failed to start conversation. Please try again.',
            icon: 'error',
            confirmButtonColor: '#1a237e'
          });
        } finally {
          setChatLoading(false);
        }
        break;

      case 'call':
        window.location.href = `tel:${property.owner.phone}`;
        break;
      case 'whatsapp':
        window.open(`https://wa.me/${property.owner.phone}`, '_blank');
        break;
      case 'email':
        window.location.href = `mailto:${property.owner.email}`;
        break;
      default:
        break;
    }
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: false,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    arrows: true
  };

  if (isLoading) {
    return (
      <div className="properties-section">
        <TopBar />
        <SkeletonStyles />
        <div className="innerHeading">
          <div className="container">
            <h1>Property Details</h1>
          </div>
        </div>
        <div className="">
          <div className="container">
            <div className="row">
              <div className="col-lg-8 mb-3">
                <div className="skeleton" style={{ width: '100%', height: '420px', borderRadius: '8px' }} />
              </div>
              <div className="col-lg-4">
                <div className="skeleton" style={{ height: '48px', width: '70%', borderRadius: '6px', marginBottom: '12px' }} />
                <div className="skeleton" style={{ height: '28px', width: '50%', borderRadius: '6px', marginBottom: '12px' }} />
                <div className="skeleton" style={{ height: '16px', width: '90%', borderRadius: '6px', marginBottom: '8px' }} />
                <div className="skeleton" style={{ height: '16px', width: '85%', borderRadius: '6px', marginBottom: '8px' }} />
                <div className="skeleton" style={{ height: '16px', width: '80%', borderRadius: '6px', marginBottom: '8px' }} />
                <div className="skeleton" style={{ height: '44px', width: '100%', borderRadius: '8px', marginTop: '16px' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!property) return null;

  const images = Array.isArray(property?.images) ? property.images : [];
  const cover = images[0]?.image ? (base_url + images[0].image) : '/images/homehive.jpg';
  const effectivePrice = property?.monthly_rental ?? property?.price_per_night ?? property?.price ?? 0;
  const isLodge = property?.property_type === 'lodge';
  const available = property?.availability_status === 'available';

  const featuresByType = () => {
    const type = (property?.property_type || '').toLowerCase();
    const items = [];
    if (type === 'apartment' || type === 'full_house' || type === 'house') {
      if (property?.bedrooms != null) items.push({ icon: 'fa-bed', label: `${property.bedrooms} bedrooms` });
      if (property?.type_of_bathroom) items.push({ icon: 'fa-bath', label: `${property.type_of_bathroom} bathroom` });

    } else if (type === 'room') {
      if (property?.type_of_bathroom != null) items.push({ icon: 'fa-bath', label: `${property.type_of_bathroom} bathroom` });

    } else if (type === 'boarding' || type === 'boarding_house') {
      if (property?.max_occupants) items.push({ icon: 'fa-users', label: `${property.max_occupants} Bed spaces(max)` });
      if (property?.rooms_available) items.push({ icon: 'fa-door-open', label: `${property.rooms_available} bed spaces available` });
      if (property?.gender_restriction) items.push({ icon: 'fa-users', label: `${property.gender_restriction} only.` });

    }
    return items;
  };

  const amenitiesByType = () => {
    const type = (property?.property_type || '').toLowerCase();
    const items = [];
    const pushIf = (cond, icon, label) => { if (cond) items.push({ icon, label }); };

    // Common flags we try to infer
    const flags = {
      wifi: property?.has_wifi || property?.has_internet || property?.internet,
      furnished: property?.furnished || property?.is_furnished,
      parking: (property?.has_parking_space || 0) > 0,
      has_outdoor_space: property?.has_outdoor_space || property?.has_garden,
      security: property?.security || property?.has_security,
      water: property?.water_source || property?.has_borehole || property?.reliable_water,
      power: property?.has_power_backup || property?.has_solar,
      backup_power: property?.power_backup,
      ac: property?.has_air_conditioning || property?.ac,
      kitchen: property?.has_kitchen,
      ensuite: property?.ensuite,
      parking_slots: property?.parking_slots,
      has_pool: property?.has_pool,
      has_gym: property?.has_gym,
      has_cctv: property?.has_cctv,
      pets_allowed: property?.pets_allowed,
      has_electricity: property?.has_electricity,
      has_restaurant: property?.has_restaurant,
      has_bar: property?.has_bar,
      has_tv: property?.has_tv,
      has_study_area: property?.has_study_area,
    };

    if (type === 'apartment' || type === 'full_house' || type === 'house') {
      pushIf(flags.furnished, 'fa-couch', 'Furnished');
      pushIf(flags.parking, 'fa-square-parking', `Parking for ${flags.parking_slots} vehicles only`);
      pushIf(flags.has_outdoor_space, 'fa-seedling', 'Garden/Yard');
      pushIf(flags.security, 'fa-shield-halved', 'Security');
      pushIf(flags.water, 'fa-faucet', `${flags.water}`);
      pushIf(flags.power, 'fa-solar-panel', `Backup power (${flags.backup_power})`);
      pushIf(flags.ac, 'fa-snowflake', 'Air conditioning');
      pushIf(flags.kitchen, 'fa-kitchen-set', 'Fitted kitchen');
      pushIf(flags.ensuite, 'fa-toilet', 'Ensuite');
      pushIf(flags.has_gym, 'fa-pool', 'Gym');
      pushIf(flags.has_cctv, 'fa-surveillance', 'CCTV');
      pushIf(flags.pets_allowed, 'fa-dog', 'Pets are allowed');


    } else if (type === 'room') {
      pushIf(flags.furnished, 'fa-couch', 'Furnished');
      pushIf(flags.parking, 'fa-square-parking', `Parking for ${flags.parking_slots} vehicles only`);
      pushIf(flags.water, 'fa-faucet', `${flags.water}`);
      pushIf(flags.has_electricity, 'fa-plug', 'Electricity')
      pushIf(flags.power, 'fa-solar-panel', `Backup power (${flags.backup_power})`);
    } else if (type === 'lodge') {
      pushIf(flags.wifi, 'fa-wifi', 'Wi‑Fi');
      pushIf(flags.ac, 'fa-snowflake', 'Air conditioning');
      pushIf(flags.ensuite, 'fa-toilet', 'Private bathroom');
      pushIf(flags.parking, 'fa-square-parking', `Guest Parking`);
      pushIf(flags.security, 'fa-shield-halved', 'Security');
      pushIf(flags.has_restaurant, 'fa-food', 'Restaurant');
      pushIf(flags.has_bar, 'fa-bar', 'Bar');
      pushIf(flags.has_tv, 'fa-tv', 'TV');
      pushIf(flags.has_pool, 'fa-shield-halved', 'Swimming Pool');


    } else if (type === 'boarding' || type === 'boarding_house') {
      pushIf(flags.wifi, 'fa-wifi', 'Wi‑Fi');
      pushIf(flags.kitchen, 'fa-kitchen-set', 'Shared kitchen');
      pushIf(flags.has_study_area, 'fa-book', 'Study Area');
      pushIf(property?.laundry, 'fa-soap', 'Laundry area');
      pushIf(flags.security, 'fa-shield-halved', 'Security');
    }

    return items;
  };

  return (
    <>
      <TopBar />
    <div className="properties-section" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      
      <div className="container" style={{ paddingTop: '24px', paddingBottom: '16px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '22px', color: 'navy' }}>{property?.title || property?.property_type_display || 'Property'}</h3>
            <div style={{ marginTop: '6px', color: '#334155' }}>
              <i className="fas fa-map-marker-alt" />
              <span style={{ marginLeft: 6 }}>{property?.location}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button className="btn btn-light" style={{ border: '1px solid navy', color: 'navy' }} onClick={handleShare}>
              <i className="fas fa-share-alt me-1" /> Share
            </button>
            <button className="btn btn-light" style={{ border: '1px solid navy', color: 'navy' }} onClick={handleAddToFavorites} disabled={isAddingFavorite}>
              {isAddingFavorite ? (<><i className="fas fa-spinner fa-spin me-1" />Saving</>) : (<><i className="far fa-heart me-1" /> Save</>)}
            </button>
          </div>
        </div>
      </div>

      <div className="container" style={{ paddingTop: '16px', flex: 1 }}>
        {/* Top layout: image left, summary card right on large screens */}
        <div className="row">
          <div className="col-lg-7">
            {/* Main image (show full image) */}
            <div style={{ position: 'relative', width: '100%', height: 0, paddingBottom: '56.25%', overflow: 'hidden', borderRadius: '12px', background: '#0b0b0b' }}>
              <img
                src={images[selectedIndex]?.image ? (base_url + images[selectedIndex].image) : cover}
                alt={property?.title || `Image ${selectedIndex + 1}`}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>

            {/* Thumbnail gallery below */}
            {images.length > 1 && (
              <div className="row" style={{ marginTop: '10px' }}>
                {images.map((img, idx) => (
                  <div key={`thumb-${idx}`} className="col-3 col-sm-2 col-md-2 col-lg-2" style={{ marginBottom: '10px' }}>
                    <button
                      type="button"
                      onClick={() => setSelectedIndex(idx)}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: 0,
                        border: selectedIndex === idx ? '2px solid #111827' : '1px solid #e5e7eb',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        background: '#fff'
                      }}
                      aria-label={`View image ${idx + 1}`}
                    >
                      <div style={{ position: 'relative', width: '100%', height: 0, paddingBottom: '100%' }}>
                        <img
                          src={base_url + img.image}
                          alt={`Thumb ${idx + 1}`}
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sticky booking/contact card on the right */}
          <div className="col-lg-5">
            <div className="card p-3" style={{ position: 'sticky', top: '20px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', borderRadius: '12px', border: '1px solid rgba(0,0,128,0.15)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <div style={{ fontSize: '22px', fontWeight: 700, color: 'navy' }}>{formatPrice(effectivePrice)}<span style={{ fontSize: '14px', fontWeight: 500, color: '#334155' }}> {isLodge ? '/night' : '/month'}</span></div>
                <span className={`availability-badge ${available ? 'available' : 'not-available'}`} style={{ padding: '4px 10px', borderRadius: '16px', background: available ? 'navy' : '#e11d48', color: '#fff' }}>{available ? 'Available' : 'Occupied'}</span>
              </div>

              {property?.owner && (
                <div className="mt-3">
                  <div className="d-grid gap-2">
                    <button
                      className="contact-btn chat-btn"
                      onClick={() => handleContactClick('chat', property.id)}
                      disabled={chatLoading}
                    >
                      {chatLoading ? (
                        <div className="beat-loader">
                          <div></div><div></div><div></div>
                        </div>
                      ) : (
                        <>
                          <i className="fas fa-comments"></i>
                          <span>Chat</span>
                        </>
                      )}
                    </button>
                    {property.owner.phone && (
                      <button onClick={() => handleContactClick('call', property.id)} className="btn" style={{ backgroundColor: 'navy', color: '#fff' }}>
                        <i className="fas fa-phone me-2" /> Call
                      </button>
                    )}
                    {property.owner.phone && (
                      <button onClick={() => handleContactClick('whatsapp', property.id)} className="btn btn-light" style={{ border: '1px solid navy', color: 'navy' }} target="_blank" rel="noreferrer">
                        <i className="fab fa-whatsapp me-2" /> WhatsApp
                      </button>
                    )}

                  </div>
                </div>
              )}

              <div className="mt-3" style={{ fontSize: '12px', color: '#334155' }}>
                Contact the landlord to arrange viewing.
              </div>

            </div>
          </div>
        </div>

        {/* Details & amenities lower section */}
        <div className="row" style={{ marginTop: '35px' }}>
          <div className="col-lg-7">
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px', backgroundColor: '#fff' }}>
              {/* Description, Key features and amenities side by side on large screens */}
              <div className="row d-none d-lg-flex" style={{ marginBottom: '16px' }}>
                <div className="col-lg-4">
                  {/* Description */}
                  <div style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '16px', marginBottom: '16px' }}>
                    <h3 style={{ fontSize: '20px', color: 'navy' }}>Description</h3>
                    <p style={{ margin: 0, lineHeight: 1.8 }}>{property?.description || 'No description provided.'}</p>
                  </div>
                </div>

                <div className="col-lg-4">
                  {/* Key features */}
                  {featuresByType().length > 0 && (
                    <div style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '16px', marginBottom: '16px' }}>
                      <h3 style={{ fontSize: '20px', color: 'navy' }}>Key features</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {featuresByType().map((f, idx) => (
                          <div key={`ft-${idx}`} style={{ color: '#334155' }}>
                            <i className={`fas ${f.icon} me-2`} />{f.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="col-lg-4">
                  {/* Amenities */}
                  {amenitiesByType().length > 0 && (
                    <div style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '16px', marginBottom: '16px' }}>
                      <h3 style={{ fontSize: '20px', color: 'navy' }}>Amenities</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {amenitiesByType().map((a, idx) => (
                          <div key={`ta-${idx}`} style={{ color: '#334155' }}>
                            <i className={`fas ${a.icon} me-2`} />{a.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* On small screens: Description first, then Key features and amenities side by side */}
              <div className="d-lg-none" style={{ marginBottom: '16px' }}>
                {/* Description on small screens */}
                <div style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '16px', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '18px', color: 'navy' }}>Description</h3>
                  <p style={{ margin: 0, lineHeight: 1.6, fontSize: '14px' }}>{property?.description || 'No description provided.'}</p>
                </div>

                {/* Key features and amenities side by side */}
                <div className="row">
                  <div className="col-6">
                    {featuresByType().length > 0 && (
                      <div style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '16px', marginBottom: '16px' }}>
                        <h3 style={{ fontSize: '18px', color: 'navy' }}>Key features</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {featuresByType().map((f, idx) => (
                            <div key={`ft-sm-${idx}`} style={{ color: '#334155', fontSize: '14px' }}>
                              <i className={`fas ${f.icon} me-1`} />{f.label}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="col-6">
                    {amenitiesByType().length > 0 && (
                      <div style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '16px', marginBottom: '16px' }}>
                        <h3 style={{ fontSize: '18px', color: 'navy' }}>Amenities</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {amenitiesByType().map((a, idx) => (
                            <div key={`ta-sm-${idx}`} style={{ color: '#334155', fontSize: '14px' }}>
                              <i className={`fas ${a.icon} me-1`} />{a.label}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Additional amenities from backend */}
              {property?.amenities?.length > 0 && (
                <div style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '16px', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '20px', color: 'navy' }}>What this place offers</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px' }}>
                    {property.amenities.map((a, idx) => (
                      <div key={idx} style={{ color: '#334155' }}><i className="fas fa-check me-2" />{a}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Related properties */}
        <div className="container" style={{ marginTop: '32px', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '22px', color: 'navy', marginBottom: '12px' }}>Related properties</h3>
          {isLoadingRelated ? (
            <div className="row">
              {Array.from({ length: 6 }).map((_, idx) => (
                <div key={`rel-sk-${idx}`} className="col-6 col-md-4" style={{ marginBottom: '16px' }}>
                  <div className="skeleton" style={{ width: '100%', height: '160px', borderRadius: '8px' }} />
                  <div className="skeleton" style={{ width: '70%', height: '16px', marginTop: '8px', borderRadius: '4px' }} />
                  <div className="skeleton" style={{ width: '40%', height: '14px', marginTop: '6px', borderRadius: '4px' }} />
                </div>
              ))}
            </div>
          ) : (
            <div className="row">
              {related.map(p => (
                <div key={p.id} className="col-6 col-md-3" style={{ marginBottom: '16px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      navigate(`/properties/${p.id}`);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    style={{ width: '100%', padding: 0, border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', background: '#fff', textAlign: 'left' }}
                  >
                    <div style={{ position: 'relative', width: '100%', height: 0, paddingBottom: '66%', overflow: 'hidden' }}>
                      <img
                        src={p.images && p.images[0]?.image ? (base_url + p.images[0].image) : '/images/homehive.jpg'}
                        alt={p.location}
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      {p.is_available != null && (
                        <span className={`availability-badge ${p.is_available ? 'available' : 'not-available'}`} style={{ position: 'absolute', top: 8, left: 8, padding: '2px 8px', borderRadius: '12px', background: p.is_available ? 'navy' : '#e11d48', color: '#fff', fontSize: '12px' }}>
                          {p.is_available ? 'Available' : 'Occupied'}
                        </span>
                      )}
                    </div>
                    <div style={{ padding: '8px 10px' }}>
                      <div style={{ fontWeight: 600, color: '#111827' }}>{p.property_type_display || p.title || 'Property'}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}><i className="fas fa-map-marker-alt" /> <span style={{ marginLeft: 4 }}>{p.location}</span></div>
                      <div style={{ marginTop: '4px', color: 'navy', fontWeight: 700 }}>
                        ${p.monthly_rental ?? p.price_per_night ?? p.price}
                        <span style={{ fontSize: '12px', color: '#334155', fontWeight: 500 }}> {p.property_type === 'lodge' ? '/night' : '/month'}</span>
                      </div>
                    </div>
                  </button>
                </div>
              ))}
              {!related.length && (
                <div className="col-12" style={{ color: '#334155' }}>No related properties found.</div>
              )}
            </div>
          )}
          
          {/* Load more indicator */}
          {nextUrl && !isLoadingRelated && (
            <div className="row" style={{ marginTop: '16px' }}>
              <div className="col-12 text-center">
                {isLoadingMore ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ padding: '20px' }}>
                    <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span style={{ marginLeft: '12px', color: '#334155' }}>Loading more properties...</span>
                  </div>
                ) : (
                  <button 
                    className="btn btn-outline-primary" 
                    onClick={loadMoreRelated}
                    style={{ padding: '8px 24px', borderRadius: '8px' }}
                  >
                    Load More Properties
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
    <Footer />

    {/* Subscription Modal */}
    <Modal
      isOpen={showSubscriptionModal}
      onRequestClose={() => setShowSubscriptionModal(false)}
      className="subscription-modal"
      overlayClassName="modal-overlay"
      ariaHideApp={false}
    >
      <div className="subscription-header">
        <h2 style={{fontSize: '20px'}}>Subscribe to Contact Landlords</h2>
        <button onClick={() => setShowSubscriptionModal(false)} className="close-button">
          <i className="fas fa-times"></i>
        </button>
      </div>
      <div className="subscription-content">
        <div className="subscription-plan">
          {subscriptionPlans.map((plan) => (
            <div key={plan.id} className="plan-card">
              <div className="plan-header">
                <h3 style={{fontSize: '18px'}}>{plan.name}</h3>
                <div style={{color: '#fff', backgroundColor: 'navy', padding: '10px', borderRadius: '5px'}} className="plan-price">
                  <span className="currency">$</span>
                  <span style={{fontSize: '20px'}} className="amount">{plan.price}</span>
                  <span className="duration">{plan.duration}</span>
                </div>
              </div>
              <div className="plan-features">
                {plan.features.map((feature, index) => (
                  <div key={index} className="feature-item">
                    <i className="fas fa-check"></i>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
              
              <div className="payment-methods">
                <h4 style={{fontSize: '18px'}}>Select Payment Method</h4>
                <div className="payment-options">
                  <button
                    className={`payment-option ${paymentMethod === 'ecocash' ? 'selected' : ''}`}
                    onClick={() => handlePaymentMethodChange('ecocash')}
                    disabled={isProcessingPayment}
                  >
                    <img src="data:image/png;base64,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" alt="EcoCash" />
                    <span>EcoCash</span>
                  </button>
                  <button
                    className={`payment-option ${paymentMethod === 'visa' ? 'selected' : ''}`}
                    onClick={() => handlePaymentMethodChange('visa')}
                    disabled={isProcessingPayment}
                  >
                    <img src="data:image/png;base64,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" alt="Visa" />
                    <span>Visa Card</span>
                  </button>
                </div>
              </div>

              <button 
                className="subscribe-btn"
                onClick={() => handleSubscribe(plan.id)}
                disabled={isProcessingPayment || !paymentMethod}
              >
                {isProcessingPayment ? (
                  <span className="processing">
                    <i className="fas fa-spinner fa-spin"></i>
                    Processing...
                  </span>
                ) : (
                  'Subscribe Now'
                )}
              </button>
            </div>
          ))}
        </div>
      </div>
    </Modal>
    </>
  );
};

export default Property;